import { useEffect, useState } from 'react';
import { trpc } from '../lib/trpc';
import webPackageJson from '../../package.json';

interface ServerHealthCheckProps {
  children: React.ReactNode;
}

export function ServerHealthCheck({ children }: ServerHealthCheckProps) {
  const [isChecking, setIsChecking] = useState(true);
  const [serverDown, setServerDown] = useState(false);
  const [attempts, setAttempts] = useState(0);

  const { refetch } = trpc.util.getVersion.useQuery(undefined, {
    enabled: false,
    retry: false,
  });

  useEffect(() => {
    const checkServer = async () => {
      let currentAttempt = 0;
      const maxAttempts = 3;

      while (currentAttempt < maxAttempts) {
        currentAttempt++;
        setAttempts(currentAttempt);

        try {
          const result = await refetch();
          if (result.data) {
            console.log(`[Version] Web: v${webPackageJson.version} | Server: v${result.data.version}`);
            setIsChecking(false);
            return;
          }
        } catch (error) {
          console.error(`Server check attempt ${currentAttempt} failed:`, error);
        }

        if (currentAttempt < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      setServerDown(true);
      setIsChecking(false);
    };

    checkServer();
  }, [refetch]);

  if (isChecking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-brand">
        <div className="text-center">
          <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-white border-t-transparent mx-auto"></div>
          <p className="text-white text-lg">Connecting to server...</p>
          <p className="text-white/70 text-sm mt-2">Attempt {attempts} of 3</p>
        </div>
      </div>
    );
  }

  if (serverDown) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-brand">
        <div className="text-center max-w-md px-8">
          <div className="mb-6">
            <svg
              className="mx-auto h-16 w-16 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-white mb-4">Server is Down</h1>
          <p className="text-white/80 mb-6">
            We couldn't connect to the server after multiple attempts. Please try again later or contact support if the problem persists.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-white text-brand rounded-full font-medium hover:bg-white/90 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
